import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { navigate } from '@reach/router';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SearchableSelect from '../common/SearchableSelect';
import ThemedConfirmDialog from '../common/ThemedConfirmDialog';
import ILocalAGGrid, {
  actionMenuItem,
  getActionsColumn,
  getDefaultColDef,
  openAgGridContextMenu,
} from '../grid/iLocalAGGrid';
import LOCATION_TYPE from '../enum/location_type';
import { getNonRxAccountIdFromSession, getNonRxAuthToken } from '../../utils/nonRxProductApi';
import {
  fetchEntitlements,
  findEntitlement,
  isFeatureUsable,
} from '../../utils/productLicensing';
import { MarketplaceBinsHelpButton, MarketplaceBinsHelpGuide } from './MarketplaceBinsHelpGuide';
import { printPickLists } from './pickList';
import './MarketplaceBins.css';

const MARKETPLACE_PAGE_SIZE = 5;
const MARKETPLACE_PAGE_SIZE_OPTIONS = [5, 10, 20, 25];

const authHeaders = () => ({
  Authorization: `Bearer ${getNonRxAuthToken()}`,
  'Content-Type': 'application/json',
});

const api = async (path, options = {}) => {
  const response = await fetch(`${process.env.REACT_APP_BASEURL}api/marketplace${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }
  return data;
};

const fail = (err) => toast.error((err && err.message) || 'Request failed');

const MarketplaceBins = ({ t }) => {
  const [boxes, setBoxes] = useState([]);
  const [boxId, setBoxId] = useState('');
  const [slots, setSlots] = useState([]);
  const [refills, setRefills] = useState([]);
  const [audit, setAudit] = useState([]);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [binId, setBinId] = useState('');
  const [availableBins, setAvailableBins] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);
  const [minQty, setMinQty] = useState('1');
  const [maxQty, setMaxQty] = useState('');
  const [boh, setBoh] = useState('0');
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adjustSlot, setAdjustSlot] = useState(null);
  const [adjustBoh, setAdjustBoh] = useState('');
  const [unloadSlot, setUnloadSlot] = useState(null);
  const [unloadReason, setUnloadReason] = useState('');
  const [releaseSlot, setReleaseSlot] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [pageTab, setPageTab] = useState('reservation');
  const defaultColDef = useMemo(() => getDefaultColDef(), []);
  const selectedBox = useMemo(
    () => boxes.find((box) => String(box.id) === String(boxId)) || null,
    [boxes, boxId]
  );
  const locationKnown = Boolean(selectedBox);
  const isKioskLocation =
    locationKnown &&
    String(selectedBox.location_type_id || selectedBox.locationtype_id || '') === LOCATION_TYPE.Kiosk;

  const loadBoxes = useCallback(async () => {
    const accountId = getNonRxAccountIdFromSession();
    if (!accountId) return;
    const response = await fetch(
      `${process.env.REACT_APP_BASEURL}getBoxesByStoreId/${accountId}`,
      { headers: authHeaders() }
    );
    if (!response.ok) return;
    const data = await response.json();
    const list = Array.isArray(data) ? data : data.results || data.boxes || [];
    setBoxes(list);
    setBoxId((current) => {
      if (current && list.some((box) => String(box.id) === String(current))) return current;
      const preferredId = localStorage.getItem('selectedLocation');
      const preferred = list.find((box) => String(box.id) === String(preferredId));
      return (preferred && preferred.id) || (list[0] && list[0].id) || '';
    });
  }, []);

  const loadSlots = useCallback(async (nextBoxId) => {
    if (!nextBoxId) return;
    setLoading(true);
    try {
      const data = await api(`/slots?box_id=${encodeURIComponent(nextBoxId)}`);
      setSlots(data.slots || []);
      setRefills(data.refills || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async (nextBoxId) => {
    if (!nextBoxId) return;
    const data = await api(`/audit?box_id=${encodeURIComponent(nextBoxId)}`);
    setAudit(data.audit || []);
  }, []);

  const loadBins = useCallback(async (nextBoxId) => {
    if (!nextBoxId) {
      setAvailableBins([]);
      return;
    }
    const data = await api(`/bins?box_id=${encodeURIComponent(nextBoxId)}`);
    const bins = data.bins || [];
    setAvailableBins(bins);
    setBinId((current) => (bins.some((bin) => bin.id === current) ? current : ''));
  }, []);

  const searchProducts = useCallback(async (text) => {
    const accountId = getNonRxAccountIdFromSession();
    const response = await fetch(`${process.env.REACT_APP_BASEURL}api/non-rx-products`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        account_id: accountId,
        search: text || '',
        status: 'active',
        pageSize: 200,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) setProducts(data.results || []);
  }, []);

  useEffect(() => {
    fetchEntitlements()
      .then((data) => {
        const entitlements = (data && data.entitlements) || [];
        const marketplace = isFeatureUsable(findEntitlement(entitlements, 'otc_delivery'));
        const dispense = isFeatureUsable(findEntitlement(entitlements, 'kiosk_marketplace'));
        setLocked(!(marketplace && dispense));
      })
      .catch(() => {});
    loadBoxes().catch(fail);
    searchProducts('').catch(() => {});
  }, [loadBoxes, searchProducts]);

  useEffect(() => {
    if (!boxId || locked || !isKioskLocation) return;
    loadSlots(boxId).catch(fail);
    loadBins(boxId).catch(fail);
  }, [boxId, locked, isKioskLocation, loadSlots, loadBins]);

  useEffect(() => {
    if (isKioskLocation) return;
    setSlots([]);
    setRefills([]);
    setAudit([]);
    setAvailableBins([]);
    setBinId('');
  }, [isKioskLocation]);

  useEffect(() => {
    if (!boxId || locked || !isKioskLocation) return undefined;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      loadSlots(boxId).catch(() => {});
    };
    const timer = window.setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [boxId, locked, isKioskLocation, loadSlots]);

  useEffect(() => {
    if (pageTab !== 'audit' || !boxId || locked || !isKioskLocation) return;
    loadAudit(boxId).catch(fail);
  }, [pageTab, boxId, locked, isKioskLocation, loadAudit]);

  const reserve = async () => {
    if (!isKioskLocation) return;
    setSaving(true);
    try {
      await api('/slots', {
        method: 'POST',
        body: JSON.stringify({
          box_id: boxId,
          bin_id: binId,
          non_rx_product_id: productId,
          min_qty: Number(minQty),
          max_qty: Number(maxQty),
          boh: Number(boh),
        }),
      });
      setBinId('');
      setProductId('');
      setMinQty('1');
      setMaxQty('');
      setBoh('0');
      toast.success(t('MarketplaceBins.Reserved', { defaultValue: 'Bin reserved.' }));
      await loadSlots(boxId);
      await loadBins(boxId);
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const saveAdjust = async () => {
    if (!adjustSlot) return;
    setSaving(true);
    try {
      await api(`/slots/${adjustSlot.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          box_id: boxId,
          boh: Number(adjustBoh),
          reason: 'Manual count',
        }),
      });
      setAdjustSlot(null);
      toast.success(t('MarketplaceBins.Adjusted', { defaultValue: 'Balance updated.' }));
      await loadSlots(boxId);
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const saveUnload = async () => {
    if (!unloadSlot) return;
    if (!unloadReason.trim()) {
      toast.error(t('MarketplaceBins.UnloadReasonRequired', { defaultValue: 'Enter a reason to unload this bin.' }));
      return;
    }
    setSaving(true);
    try {
      await api(`/slots/${unloadSlot.id}/unload`, {
        method: 'POST',
        body: JSON.stringify({ reason: unloadReason.trim() }),
      });
      setUnloadSlot(null);
      toast.success(t('MarketplaceBins.Unloaded', { defaultValue: 'Bin unloaded.' }));
      await loadSlots(boxId);
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmRelease = async () => {
    if (!releaseSlot) return;
    setSaving(true);
    try {
      await api(`/slots/${releaseSlot.id}?box_id=${encodeURIComponent(boxId)}`, {
        method: 'DELETE',
      });
      setReleaseSlot(null);
      toast.success(t('MarketplaceBins.Released', { defaultValue: 'Bin released.' }));
      await loadSlots(boxId);
      await loadBins(boxId);
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setSaving(true);
    try {
      await api(`/refills/${cancelTarget.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ box_id: boxId }),
      });
      setCancelTarget(null);
      toast.success(t('MarketplaceBins.RefillCancelled', { defaultValue: 'Refill cancelled.' }));
      await loadSlots(boxId);
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = useCallback(
    (status) => {
      if (status === 'variance_hold') {
        return t('MarketplaceBins.VarianceHold', { defaultValue: 'Variance hold' });
      }
      if (status === 'paused') return t('MarketplaceBins.Paused', { defaultValue: 'Paused' });
      if (status === 'unloaded') return t('MarketplaceBins.Unloaded', { defaultValue: 'Unloaded' });
      if (status === 'active') return t('MarketplaceBins.Active', { defaultValue: 'Active' });
      if (status === 'in_progress') return t('MarketplaceBins.InProgress', { defaultValue: 'In progress' });
      if (status === 'open') return t('MarketplaceBins.Open', { defaultValue: 'Open' });
      return status || '';
    },
    [t]
  );

  const openRefillCount = refills.reduce(
    (sum, refill) => sum + ((refill.lines && refill.lines.length) || 0),
    0
  );

  const refillRows = useMemo(
    () =>
      refills
        .flatMap((refill) =>
          (refill.lines || []).map((line) => ({
            ...line,
            refill_id: refill.id,
            order_code: refill.order_code,
            order_status: refill.status,
            statusName: statusLabel(refill.status),
          }))
        )
        .sort((left, right) => String(left.bin_id || '').localeCompare(String(right.bin_id || ''))),
    [refills, statusLabel]
  );

  const slotAlertLabel = useCallback(
    (slot) => {
      if (slot && slot.refill_reason === 'out_of_stock') {
        return t('MarketplaceBins.OutOfStock', { defaultValue: 'Out of stock' });
      }
      if (slot && (slot.refill_reason === 'below_min' || Number(slot.boh) < Number(slot.min_qty))) {
        return t('MarketplaceBins.BelowMinimum', { defaultValue: 'Below minimum' });
      }
      return statusLabel(slot && slot.status);
    },
    [statusLabel, t]
  );

  const slotRows = useMemo(
    () =>
      slots.map((slot) => ({
        ...slot,
        productName: slot.product ? slot.product.description : '',
        statusName: slotAlertLabel(slot),
        belowMinimum:
          slot.refill_reason === 'below_min' ||
          slot.refill_reason === 'out_of_stock' ||
          Number(slot.boh) < Number(slot.min_qty),
      })),
    [slots, slotAlertLabel]
  );

  const getSlotRowStyle = useCallback((params) => {
    if (params && params.data && params.data.belowMinimum) {
      return {
        background: '#fff7ed',
        boxShadow: 'inset 4px 0 0 #ea580c',
      };
    }
    return undefined;
  }, []);

  const getContextMenuItems = useCallback(
    (params) => {
      const colId =
        params &&
        params.column &&
        (params.column.getColId ? params.column.getColId() : params.column.colId);
      if (colId !== 'actions') return [];
      const slot = params.node && params.node.data;
      if (!slot) return [];
      return [
        actionMenuItem({
          name: t('MarketplaceBins.Adjust', { defaultValue: 'Adjust' }),
          icon: 'edit',
          action: () => {
            setAdjustBoh(String(slot.boh));
            setAdjustSlot(slot);
          },
        }),
        actionMenuItem({
          name: t('MarketplaceBins.Unload', { defaultValue: 'Unload' }),
          icon: 'disable',
          action: () => {
            setUnloadReason('');
            setUnloadSlot(slot);
          },
        }),
        actionMenuItem({
          name: t('MarketplaceBins.Release', { defaultValue: 'Release' }),
          icon: 'delete',
          action: () => setReleaseSlot(slot),
        }),
      ];
    },
    [t]
  );

  const openRowActionsMenu = useCallback(
    (event, params, menuHelpers) => {
      const items = getContextMenuItems(params);
      if (menuHelpers && typeof menuHelpers.openMenu === 'function') {
        menuHelpers.openMenu(items);
        return;
      }
      openAgGridContextMenu(params, (menuHelpers && menuHelpers.positionEvent) || event);
    },
    [getContextMenuItems]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'bin_id',
        headerName: t('MarketplaceBins.Bin', { defaultValue: 'Bin' }),
        maxWidth: 140,
        minWidth: 110,
      },
      {
        field: 'load_code',
        headerName: t('MarketplaceBins.LoadCode', { defaultValue: 'Load code' }),
        minWidth: 160,
        maxWidth: 190,
      },
      {
        field: 'productName',
        headerName: t('MarketplaceBins.Product', { defaultValue: 'Product' }),
        minWidth: 220,
        flex: 1,
      },
      {
        field: 'boh',
        headerName: t('MarketplaceBins.BalanceOnHand', { defaultValue: 'Balance on hand' }),
        maxWidth: 180,
        minWidth: 150,
        filter: false,
      },
      {
        field: 'min_qty',
        headerName: t('MarketplaceBins.Minimum', { defaultValue: 'Minimum' }),
        maxWidth: 140,
        minWidth: 120,
        filter: false,
      },
      {
        field: 'max_qty',
        headerName: t('MarketplaceBins.Maximum', { defaultValue: 'Maximum' }),
        maxWidth: 140,
        minWidth: 120,
        filter: false,
      },
      {
        field: 'statusName',
        headerName: t('MarketplaceBins.Status', { defaultValue: 'Status' }),
        maxWidth: 170,
        minWidth: 140,
      },
      getActionsColumn({
        headerName: t('MarketplaceBins.Actions', { defaultValue: 'Actions' }),
        onActionsClick: (event, params, menuHelpers) =>
          openRowActionsMenu(event, params, menuHelpers),
      }),
    ],
    [openRowActionsMenu, t]
  );

  const refillColumnDefs = useMemo(
    () => [
      {
        field: 'order_code',
        headerName: t('MarketplaceBins.LoadCode', { defaultValue: 'Load code' }),
        minWidth: 150,
        maxWidth: 170,
      },
      {
        field: 'product_name',
        headerName: t('MarketplaceBins.Product', { defaultValue: 'Product' }),
        minWidth: 220,
        flex: 1,
      },
      {
        field: 'bin_id',
        headerName: t('MarketplaceBins.Bin', { defaultValue: 'Bin' }),
        maxWidth: 120,
      },
      {
        field: 'boh',
        headerName: t('MarketplaceBins.BalanceOnHand', { defaultValue: 'Balance on hand' }),
        maxWidth: 180,
        filter: false,
        valueGetter: (params) => {
          const row = (params && params.data) || {};
          return row.boh != null ? row.boh : row.boh_at_create;
        },
      },
      {
        field: 'suggested_qty',
        headerName: t('MarketplaceBins.Load', { defaultValue: 'Load' }),
        maxWidth: 120,
        filter: false,
      },
      {
        field: 'statusName',
        headerName: t('MarketplaceBins.Status', { defaultValue: 'Status' }),
        maxWidth: 150,
        minWidth: 120,
      },
      {
        colId: 'cancel',
        headerName: t('MarketplaceBins.Actions', { defaultValue: 'Actions' }),
        minWidth: 160,
        maxWidth: 180,
        filter: false,
        sortable: false,
        suppressMenu: true,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: (params) => {
          const row = params && params.data;
          if (!row || row.order_status !== 'open') return '';
          return (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              sx={{ textTransform: 'none' }}
              onClick={() =>
                setCancelTarget({ id: row.refill_id, order_code: row.order_code })
              }
            >
              {t('MarketplaceBins.CancelRefill', { defaultValue: 'Cancel refill' })}
            </Button>
          );
        },
      },
    ],
    [t]
  );

  const auditRows = useMemo(
    () =>
      audit.map((row) => ({
        ...row,
        when: row.created_on ? new Date(row.created_on).toLocaleString() : '',
      })),
    [audit]
  );

  const auditColumnDefs = useMemo(
    () => [
      {
        field: 'when',
        headerName: t('MarketplaceBins.When', { defaultValue: 'When' }),
        minWidth: 190,
      },
      {
        field: 'actor_name',
        headerName: t('MarketplaceBins.Who', { defaultValue: 'Who' }),
        minWidth: 160,
      },
      {
        field: 'action',
        headerName: t('MarketplaceBins.Action', { defaultValue: 'Action' }),
        minWidth: 180,
      },
      {
        field: 'bin_id',
        headerName: t('MarketplaceBins.Bin', { defaultValue: 'Bin' }),
        maxWidth: 120,
      },
      {
        field: 'product_name',
        headerName: t('MarketplaceBins.Product', { defaultValue: 'Product' }),
        minWidth: 220,
        flex: 1,
      },
      {
        field: 'detail',
        headerName: t('MarketplaceBins.Detail', { defaultValue: 'Detail' }),
        minWidth: 180,
        flex: 1,
      },
    ],
    [t]
  );

  if (locked) {
    return (
      <Box className="marketplace-bins-page">
        <Typography variant="h6">
          {t('MarketplaceBins.LockedTitle', { defaultValue: 'License required' })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('MarketplaceBins.Locked', {
            defaultValue: 'Shop is a licensed add-on. It also requires Product Marketplace.',
          })}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          onClick={() => navigate('/home/features/kiosk_marketplace')}
        >
          {t('MarketplaceBins.ViewLicense', { defaultValue: 'View license' })}
        </Button>
      </Box>
    );
  }

  return (
    <Box className="marketplace-bins-page">
      <Box className="marketplace-bins-toolbar">
        <Typography variant="body2" className="marketplace-bins-hint">
          {t('MarketplaceBins.Hint', {
            defaultValue:
              'Reserve one bin for one Marketplace product. That bin cannot be used for prescriptions.',
          })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MarketplaceBinsHelpButton t={t} onOpen={() => setHelpOpen(true)} />
          <Box className="marketplace-bins-kiosk">
          <SearchableSelect
            id="marketplace-bins-location"
            label={t('MarketplaceBins.Location', { defaultValue: 'Location' })}
            placeholder={t('MarketplaceBins.SearchLocations', { defaultValue: 'Search locations...' })}
            variant="outlined"
            items={boxes.map((box) => ({
              id: String(box.id),
              name: box.name || String(box.id),
            }))}
            value={boxId ? String(boxId) : ''}
            onChange={(event) => setBoxId(event.target.value)}
          />
          </Box>
        </Box>
      </Box>
      {locationKnown && !isKioskLocation ? (
        <Box className="marketplace-bins-kiosk-notice" role="status">
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="body2">
            {t('MarketplaceBins.KioskOnly', {
              defaultValue:
                'Bin reservation is only for kiosk locations. Select a kiosk to use these controls.',
            })}
          </Typography>
        </Box>
      ) : null}
      {loading || saving ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : null}

      <Box className={isKioskLocation ? undefined : 'marketplace-bins-locked-controls'}>
      <Tabs
        value={pageTab}
        onChange={(event, next) => setPageTab(next)}
        className="marketplace-bins-tabs"
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab
          value="reservation"
          label={t('MarketplaceBins.ReservationTab', { defaultValue: 'Reservation' })}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
        />
        <Tab
          value="refill"
          label={t('MarketplaceBins.OpenRefillTab', {
            defaultValue: openRefillCount ? 'Open refill ({{count}})' : 'Open refill',
            count: openRefillCount,
          })}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
        />
        <Tab
          value="audit"
          label={t('MarketplaceBins.AuditTab', { defaultValue: 'Audit' })}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
        />
      </Tabs>

      {pageTab === 'refill' ? (
        <Box className="marketplace-bins-panel">
          <Box className="marketplace-bins-section-head">
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('MarketplaceBins.OpenRefillTitle', { defaultValue: 'Open refill' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('MarketplaceBins.OpenRefillHint', {
                  defaultValue: 'Each bin has its own load code. Cancel a row to stop that load.',
                })}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              sx={{ textTransform: 'none', flexShrink: 0 }}
              disabled={!isKioskLocation || !refillRows.length || saving}
              onClick={() => {
                const orders = [];
                const seen = new Set();
                refillRows.forEach((row) => {
                  if (seen.has(row.refill_id)) return;
                  seen.add(row.refill_id);
                  orders.push({
                    id: row.refill_id,
                    order_code: row.order_code,
                    lines: refillRows.filter((line) => line.refill_id === row.refill_id),
                  });
                });
                const location = boxes.find((box) => String(box.id) === String(boxId));
                printPickLists(orders, { locationName: location && location.name });
                api('/pick-list/email', {
                  method: 'POST',
                  body: JSON.stringify({ box_id: boxId }),
                }).then((result) => {
                  if (result && result.sent) {
                    toast.success(t('MarketplaceBins.PickListEmailed', {
                      defaultValue: 'Pick list printed and emailed.',
                    }));
                  }
                }).catch(() => null);
              }}
            >
              {t('MarketplaceBins.PrintPickList', { defaultValue: 'Print pick list' })}
            </Button>
          </Box>
          <Box className="marketplace-bins-grid-wrap">
            <ILocalAGGrid
              className="ag-theme-alpine"
              columnDefs={refillColumnDefs}
              defaultColDef={defaultColDef}
              rowModelType="clientSide"
              rowData={refillRows}
              pagination
              paginationPageSize={MARKETPLACE_PAGE_SIZE}
              paginationPageSizeSelector={MARKETPLACE_PAGE_SIZE_OPTIONS}
              rowHeight={52}
              overlayNoRowsMessage={t('MarketplaceBins.NoOpenRefill', {
                defaultValue: 'No open refill for this location.',
              })}
              getRowId={(params) => (params.data && params.data.id) || ''}
            />
          </Box>
        </Box>
      ) : null}

      {pageTab === 'reservation' ? (
      <Box className="marketplace-bins-split">
      <Box className="marketplace-bins-panel marketplace-bins-cart">
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('MarketplaceBins.Reserve', { defaultValue: 'Reserve a bin' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('MarketplaceBins.ReserveHint', {
            defaultValue: 'One product per bin. Maximum must be greater than minimum.',
          })}
        </Typography>
        <Box className="marketplace-bins-form">
          <SearchableSelect
            id="marketplace-bins-bin"
            label={t('MarketplaceBins.Bin', { defaultValue: 'Bin' })}
            placeholder={
              availableBins.length
                ? t('MarketplaceBins.SearchBins', { defaultValue: 'Search bins...' })
                : t('MarketplaceBins.NoOpenBins', { defaultValue: 'No bins' })
            }
            variant="outlined"
            disabled={!isKioskLocation || !availableBins.length}
            items={availableBins.map((bin) => ({
              id: bin.id,
              name: bin.bin_size ? `${bin.id} · ${bin.bin_size}` : bin.id,
            }))}
            value={binId || ''}
            onChange={(event) => setBinId(event.target.value)}
          />
          <SearchableSelect
            id="marketplace-bins-product"
            label={t('MarketplaceBins.Product', { defaultValue: 'Product' })}
            placeholder={t('MarketplaceBins.SearchProducts', { defaultValue: 'Search products...' })}
            variant="outlined"
            items={products.map((product) => {
              const bins = slots
                .filter((slot) => String(slot.non_rx_product_id || (slot.product && slot.product.id) || '') === String(product.id))
                .map((slot) => slot.bin_id)
                .filter(Boolean);
              return {
                id: String(product.id),
                name: product.description || String(product.id),
                bins,
              };
            })}
            value={productId ? String(productId) : ''}
            onChange={(event) => setProductId(event.target.value)}
            disabled={!isKioskLocation}
          />
          <TextField
            size="small"
            variant="outlined"
            type="number"
            label={t('MarketplaceBins.Minimum', { defaultValue: 'Minimum' })}
            value={minQty}
            onChange={(event) => setMinQty(event.target.value)}
            disabled={!isKioskLocation}
            inputProps={{ min: 0 }}
          />
          <TextField
            size="small"
            variant="outlined"
            type="number"
            label={t('MarketplaceBins.Maximum', { defaultValue: 'Maximum' })}
            value={maxQty}
            onChange={(event) => setMaxQty(event.target.value)}
            disabled={!isKioskLocation}
            inputProps={{ min: 1 }}
          />
          <TextField
            size="small"
            variant="outlined"
            type="number"
            label={t('MarketplaceBins.BalanceOnHand', { defaultValue: 'Balance on hand' })}
            value={boh}
            onChange={(event) => setBoh(event.target.value)}
            disabled={!isKioskLocation}
            inputProps={{ min: 0 }}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={!isKioskLocation || saving || !boxId || !binId || !productId}
            onClick={reserve}
            sx={{ textTransform: 'none', height: 40 }}
          >
            {t('MarketplaceBins.ReserveBin', { defaultValue: 'Reserve bin' })}
          </Button>
        </Box>
      </Box>
      <Box className="marketplace-bins-panel">
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('MarketplaceBins.ReservedBins', { defaultValue: 'Reserved bins' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('MarketplaceBins.LevelsHint', {
            defaultValue:
              'Refill when balance on hand drops below the minimum. The load brings it up to the maximum.',
          })}
        </Typography>
        <Box className="marketplace-bins-grid-wrap">
          <ILocalAGGrid
            className="ag-theme-alpine"
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="clientSide"
            rowData={slotRows}
            pagination
            paginationPageSize={MARKETPLACE_PAGE_SIZE}
            paginationPageSizeSelector={MARKETPLACE_PAGE_SIZE_OPTIONS}
            rowHeight={48}
            overlayNoRowsMessage={t('MarketplaceBins.Empty', {
              defaultValue: 'No reserved bins at this location yet. Add one on the left.',
            })}
            getRowId={(params) => (params.data && params.data.id) || ''}
            getRowStyle={getSlotRowStyle}
            getContextMenuItems={getContextMenuItems}
          />
        </Box>
      </Box>
      </Box>
      ) : null}

      {pageTab === 'audit' ? (
        <Box className="marketplace-bins-panel">
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('MarketplaceBins.AuditTitle', { defaultValue: 'Audit' })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('MarketplaceBins.AuditHint', {
              defaultValue: 'Who reserved, loaded, unloaded, released, or cancelled a refill at this location.',
            })}
          </Typography>
          <Box className="marketplace-bins-grid-wrap">
            <ILocalAGGrid
              className="ag-theme-alpine"
              columnDefs={auditColumnDefs}
              defaultColDef={defaultColDef}
              rowModelType="clientSide"
              rowData={auditRows}
              pagination={auditRows.length > 10}
              paginationPageSize={10}
              rowHeight={48}
              overlayNoRowsMessage={t('MarketplaceBins.AuditEmpty', {
                defaultValue: 'No marketplace activity at this location yet.',
              })}
              getRowId={(params) => (params.data && params.data.id) || ''}
            />
          </Box>
        </Box>
      ) : null}
      </Box>

      <ThemedConfirmDialog
        open={Boolean(adjustSlot)}
        title={t('MarketplaceBins.AdjustTitle', { defaultValue: 'Adjust balance on hand' })}
        message={
          adjustSlot
            ? `${adjustSlot.bin_id} · ${adjustSlot.productName || (adjustSlot.product && adjustSlot.product.description) || ''}`
            : ''
        }
        cancelLabel={t('MarketplaceBins.Cancel', { defaultValue: 'Cancel' })}
        confirmLabel={t('MarketplaceBins.Save', { defaultValue: 'Save' })}
        busy={saving}
        onCancel={() => {
          if (!saving) setAdjustSlot(null);
        }}
        onConfirm={saveAdjust}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="number"
          variant="outlined"
          label={t('MarketplaceBins.BalanceOnHand', { defaultValue: 'Balance on hand' })}
          value={adjustBoh}
          onChange={(event) => setAdjustBoh(event.target.value)}
          inputProps={{ min: 0 }}
        />
      </ThemedConfirmDialog>

      <ThemedConfirmDialog
        open={Boolean(unloadSlot)}
        title={t('MarketplaceBins.UnloadTitle', { defaultValue: 'Unload bin' })}
        message={t('MarketplaceBins.UnloadHint', {
          defaultValue: 'Sets the balance on hand to zero and records why the product was removed.',
        })}
        cancelLabel={t('MarketplaceBins.Cancel', { defaultValue: 'Cancel' })}
        confirmLabel={t('MarketplaceBins.Unload', { defaultValue: 'Unload' })}
        confirmDisabled={!unloadReason.trim()}
        busy={saving}
        onCancel={() => {
          if (!saving) setUnloadSlot(null);
        }}
        onConfirm={saveUnload}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          variant="outlined"
          label={t('MarketplaceBins.Reason', { defaultValue: 'Reason' })}
          value={unloadReason}
          onChange={(event) => setUnloadReason(event.target.value)}
        />
      </ThemedConfirmDialog>

      <ThemedConfirmDialog
        open={Boolean(releaseSlot)}
        title={t('MarketplaceBins.ReleaseTitle', { defaultValue: 'Release this bin?' })}
        message={t('MarketplaceBins.ReleaseHint', {
          defaultValue:
            'The bin can be used for prescriptions again. This is only allowed when the balance on hand is zero.',
        })}
        cancelLabel={t('MarketplaceBins.Cancel', { defaultValue: 'Cancel' })}
        confirmLabel={t('MarketplaceBins.Release', { defaultValue: 'Release' })}
        busy={saving}
        onCancel={() => {
          if (!saving) setReleaseSlot(null);
        }}
        onConfirm={confirmRelease}
      />

      <ThemedConfirmDialog
        open={Boolean(cancelTarget)}
        title={t('MarketplaceBins.CancelRefillTitle', { defaultValue: 'Cancel this refill?' })}
        message={
          cancelTarget
            ? t('MarketplaceBins.CancelRefillHint', {
                defaultValue: 'Cancel {{code}}? The location will no longer be asked to load it.',
                code: cancelTarget.order_code,
              })
            : ''
        }
        cancelLabel={t('MarketplaceBins.Close', { defaultValue: 'Close' })}
        confirmLabel={t('MarketplaceBins.CancelRefill', { defaultValue: 'Cancel refill' })}
        busy={saving}
        onCancel={() => {
          if (!saving) setCancelTarget(null);
        }}
        onConfirm={confirmCancel}
      />
      <MarketplaceBinsHelpGuide t={t} open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default withTranslation()(MarketplaceBins);



// WEBPACK FOOTER //
// ./src/components/marketplace-bins/index.js